import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';

export type CancelLimitResponseArgs = {
  id?: number;
  cancel_fee_percent: number;
  cancel_fee_amount: number;
  cancel_fee_mode?: 'fixed' | 'percent';
  limit_datetime: string;
  limit_datetime_mode?: 'days_before' | 'calendar';
  limit_datetime_timezone?: string;
  utc_offset?: number;
};

export type CancelLimitArgs = {
  id?: number;
  cancelFeePercent: number;
  cancelFeeAmount: number;
  cancelFeeMode?: 'fixed' | 'percent';
  limitDatetime: Moment;
  limitDatetimeMode?: 'daysBefore' | 'calendar';
  limitDatetimeTimezone?: string;
  utcOffset?: number;
};

export const convertCancelLimitResponseToArgs = (response: CancelLimitResponseArgs): CancelLimitArgs => {
  const limitDatetime = moment(response.limit_datetime);

  // Determine cancel fee mode if not specified
  let cancelFeeMode: 'fixed' | 'percent';
  if (response.cancel_fee_mode) {
    cancelFeeMode = response.cancel_fee_mode;
  } else {
    cancelFeeMode = response.cancel_fee_amount > 0 ? 'fixed' : 'percent';
  }

  // Determine limit datetime mode if not specified
  let limitDatetimeMode: 'daysBefore' | 'calendar';
  if (response.limit_datetime_mode) {
    limitDatetimeMode = response.limit_datetime_mode === 'days_before' ? 'daysBefore' : 'calendar';
  } else {
    // If time is 23:59, use 'daysBefore', else 'calendar'
    const hour = limitDatetime.hour();
    const minute = limitDatetime.minute();
    limitDatetimeMode = hour === 23 && minute === 59 ? 'daysBefore' : 'calendar';
  }

  return {
    id: response.id,
    cancelFeePercent: response.cancel_fee_percent,
    cancelFeeAmount: response.cancel_fee_amount,
    cancelFeeMode,
    limitDatetime,
    limitDatetimeMode,
    limitDatetimeTimezone: response.limit_datetime_timezone,
    utcOffset: response.utc_offset
  };
};

export default class CancelLimit {
  @observable id?: number;
  @observable cancelFeePercent: number;
  @observable cancelFeeAmount: number;
  @observable cancelFeeMode: 'fixed' | 'percent';
  @observable limitDatetime: Moment;
  @observable limitDatetimeMode: 'daysBefore' | 'calendar';
  @observable limitDatetimeTimezone: string;
  @observable utcOffset?: number;

  constructor(args: CancelLimitArgs) {
    this.id = args.id;
    this.cancelFeePercent = args.cancelFeePercent ?? 0;
    this.cancelFeeAmount = args.cancelFeeAmount ?? 0;
    this.cancelFeeMode = args.cancelFeeMode ?? (this.cancelFeeAmount > 0 ? 'fixed' : 'percent');
    this.limitDatetime = args.limitDatetime;
    this.limitDatetimeMode = args.limitDatetimeMode ?? 'daysBefore';
    this.limitDatetimeTimezone = args.limitDatetimeTimezone ?? 'Asia/Tokyo';
    this.utcOffset = args.utcOffset;
  }

  get timeZoneLabel() {
    if (this.limitDatetimeTimezone === 'Asia/Tokyo') {
      return null;
    }

    if (this.utcOffset) {
      const gmtOffset = this.utcOffset / 3600;
      const gmtSign = gmtOffset >= 0 ? '+' : '-';
      return `GMT${gmtSign}${Math.abs(gmtOffset).toString().padStart(2, '0')}:00`;
    }

    return this.limitDatetimeTimezone;
  }

  setCancelFeePercent(value: number) {
    this.cancelFeePercent = value;
    this.cancelFeeAmount = 0;
    this.cancelFeeMode = 'percent';
  }

  setCancelFeeAmount(value: number) {
    this.cancelFeeAmount = value;
    this.cancelFeePercent = 0;
    this.cancelFeeMode = 'fixed';
  }

  setCancelFeeMode(mode: 'fixed' | 'percent') {
    this.cancelFeeMode = mode;
    if (mode === 'fixed') {
      this.cancelFeePercent = 0;
    } else {
      this.cancelFeeAmount = 0;
    }
  }

  setLimitDatetimeMode(mode: 'daysBefore' | 'calendar') {
    this.limitDatetimeMode = mode;
  }

  setLimitDatetime(date: Moment) {
    this.limitDatetime = date;
  }

  setDaysBefore(value: number, checkinDate: Moment) {
    if (value < 0) return;
    // 指定した日の終日を対象に含めるため、23:59をデフォルトにする
    const date = checkinDate.clone().tz(this.limitDatetimeTimezone).endOf('day');
    this.limitDatetime = date.subtract(value, 'days');
  }

  daysBefore(checkinDate: Moment): number {
    return checkinDate.clone().startOf('day').diff(this.limitDatetime.clone().startOf('day'), 'days');
  }

  validationErrors(): string[] {
    const errors: string[] = [];
    if (this.cancelFeePercent < 0 || this.cancelFeePercent > 100) {
      errors.push('キャンセルポリシーのキャンセル料率は0〜100の間で指定してください。');
    }
    return errors;
  }

  get updateParams() {
    return {
      limit_datetime: this.limitDatetime.clone().tz(this.limitDatetimeTimezone).format('YYYY-MM-DD HH:mm:ss ZZ'),
      limit_datetime_timezone: this.limitDatetimeTimezone,
      limit_datetime_mode: this.limitDatetimeMode === 'daysBefore' ? 'days_before' : 'calendar',
      cancel_fee_percent: this.cancelFeePercent,
      cancel_fee_amount: this.cancelFeeAmount
    };
  }
}
